/**
 * Patient attachments, as the browser treats them (QA report §5.2).
 *
 * Mirrors backend/lib/attachments.js: the same 50 MB cap, the same suggested
 * categories and — the one that matters — the same list of types that may be
 * rendered inline. The viewer shows a blob: URL made by this page, which runs
 * with this page's origin; an uploaded HTML file or SVG opened that way would
 * be script on our origin. So a type outside the list is only ever offered as
 * a download, here and on the server. attachments-parity.test.mjs holds the
 * two lists together.
 *
 * Pure — no DOM. The canvas work lives in components/common/thumbnails.js.
 */

export const MAX_ATTACHMENT_BYTES = 50 * 1024 * 1024;

/** Long side of the thumbnail the browser makes at upload, in CSS pixels. */
export const THUMBNAIL_EDGE = 320;

export const SUGGESTED_CATEGORIES = Object.freeze([
  'روشتات',
  'نتائج التحاليل',
  'صور وتقارير الأشعة',
  'تقارير طبية',
  'نماذج التأمين الطبي',
  'أخرى',
]);

const INLINE_SAFE = /^(image\/(png|jpe?g|webp|gif|bmp|avif)|application\/pdf|video\/(mp4|webm|ogg|quicktime)|audio\/(mpeg|mp3|mp4|ogg|wav|x-wav|webm|aac)|text\/plain)$/;

/** `type/subtype`, lower-cased and without parameters — or octet-stream. */
export function cleanMime(raw) {
  const value = String(raw || '')
    .split(';')[0]
    .trim()
    .toLowerCase();
  return /^[a-z0-9][a-z0-9.+-]*\/[a-z0-9][a-z0-9.+-]*$/.test(value) && value.length <= 120
    ? value
    : 'application/octet-stream';
}

export const isInlineSafe = (mime) => INLINE_SAFE.test(cleanMime(mime));

const EXTENSION_KIND = {
  doc: 'doc', docx: 'doc', odt: 'doc', rtf: 'doc',
  xls: 'sheet', xlsx: 'sheet', ods: 'sheet', csv: 'sheet',
  zip: 'archive', rar: 'archive', '7z': 'archive', gz: 'archive',
  dcm: 'scan', dicom: 'scan',
};

/**
 * What a file is, for its icon and its viewer:
 * image | pdf | video | audio | text | doc | sheet | archive | scan | other.
 * `scan` is a DICOM study — radiology's native format, downloadable only.
 */
export function kindOf(mime, fileName = '') {
  const m = cleanMime(mime);
  if (/^image\//.test(m) && isInlineSafe(m)) return 'image';
  if (m === 'application/pdf') return 'pdf';
  if (/^video\//.test(m)) return 'video';
  if (/^audio\//.test(m)) return 'audio';
  if (m === 'text/plain') return 'text';
  if (m === 'application/dicom') return 'scan';
  const ext = String(fileName).split('.').pop().toLowerCase();
  if (EXTENSION_KIND[ext]) return EXTENSION_KIND[ext];
  if (/word|opendocument\.text|rtf/.test(m)) return 'doc';
  if (/sheet|excel|csv/.test(m)) return 'sheet';
  if (/zip|compressed|rar|7z|gzip/.test(m)) return 'archive';
  return 'other';
}

/** The badge each kind wears in the grid when it has no picture. */
export const KIND_META = Object.freeze({
  image: { label: 'صورة', icon: 'image', tone: 'cyan' },
  pdf: { label: 'PDF', icon: 'file-text', tone: 'red' },
  video: { label: 'فيديو', icon: 'film', tone: 'purple' },
  audio: { label: 'صوت', icon: 'music', tone: 'teal' },
  text: { label: 'نص', icon: 'file-text', tone: 'slate' },
  doc: { label: 'مستند', icon: 'file-text', tone: 'blue' },
  sheet: { label: 'جدول', icon: 'sheet', tone: 'emerald' },
  archive: { label: 'ملف مضغوط', icon: 'file-archive', tone: 'amber' },
  scan: { label: 'DICOM', icon: 'scan-line', tone: 'purple' },
  other: { label: 'ملف', icon: 'file', tone: 'slate' },
});

/** "12.4 م.ب" / "820 ك.ب" — sizes as a desk reads them. */
export function formatBytes(bytes) {
  const n = Number(bytes);
  if (!Number.isFinite(n) || n < 0) return '—';
  if (n < 1024) return `${n} بايت`;
  if (n < 1024 * 1024) return `${Math.round(n / 1024)} ك.ب`;
  return `${Math.round((n / (1024 * 1024)) * 10) / 10} م.ب`;
}

/**
 * Programs, installers and scripts — the one kind of file a patient's file
 * never takes. The server refuses the same list, and checks the bytes too
 * (backend/lib/attachments.js, parity-tested); this is the early, friendly no.
 */
export const EXECUTABLE_EXTENSIONS = Object.freeze([
  'exe', 'msi', 'msp', 'msix', 'appx', 'com', 'scr', 'pif', 'cpl', 'dll', 'sys',
  'bat', 'cmd', 'ps1', 'psm1', 'vbs', 'vbe', 'js', 'jse', 'wsf', 'wsh', 'hta',
  'jar', 'apk', 'app', 'dmg', 'lnk', 'reg', 'sh', 'run', 'gadget',
]);
const EXECUTABLE_MIME = /^application\/(x-msdownload|x-msdos-program|x-ms-installer|x-msi|x-ms-shortcut|x-dosexec|x-executable|x-elf|x-mach-binary|x-sh|x-shellscript|x-bat|x-java-archive|java-archive|vnd\.android\.package-archive|x-apple-diskimage|hta)$/;

/** The extension after the last dot, lower-cased — '' when there is none. */
export function extensionOf(fileName) {
  const base = String(fileName || '').split(/[\\/]/).pop();
  const dot = base.lastIndexOf('.');
  return dot > 0 ? base.slice(dot + 1).trim().toLowerCase() : '';
}

/** Is this a program, by its name or its declared type? */
export const isExecutable = ({ fileName, mimeType } = {}) =>
  EXECUTABLE_EXTENSIONS.includes(extensionOf(fileName)) || EXECUTABLE_MIME.test(cleanMime(mimeType));

/** Why a file cannot be uploaded, or null. */
export function validateFile(file) {
  if (!file) return 'اختر ملفاً';
  if (!file.size) return `الملف "${file.name}" فارغ`;
  if (file.size > MAX_ATTACHMENT_BYTES) {
    return `الملف "${file.name}" (${formatBytes(file.size)}) أكبر من الحد المسموح 50 م.ب`;
  }
  if (isExecutable({ fileName: file.name, mimeType: file.type })) {
    return `الملف "${file.name}" برنامج تنفيذي، والبرامج لا تُرفع في ملف المريض. ارفع مستنداً أو صورة أو PDF.`;
  }
  return null;
}

/** Width and height scaled so the long side is at most `edge`, never upscaled. */
export function fitWithin(width, height, edge = THUMBNAIL_EDGE) {
  const w = Number(width) || 0;
  const h = Number(height) || 0;
  if (!w || !h) return { width: 0, height: 0 };
  const scale = Math.min(1, edge / Math.max(w, h));
  return { width: Math.max(1, Math.round(w * scale)), height: Math.max(1, Math.round(h * scale)) };
}

/** Can the browser make a picture of this file at upload time? */
export const canThumbnail = (mime, fileName) => ['image', 'video'].includes(kindOf(mime, fileName));

/** A request's own documents first, then the rest of the patient's, newest first within each. */
export function orderForRequest(rows, serviceRequestId) {
  const list = Array.isArray(rows) ? [...rows] : [];
  const mine = (r) => (serviceRequestId && Number(r.serviceRequestId) === Number(serviceRequestId) ? 0 : 1);
  return list.sort((a, b) => mine(a) - mine(b) || Date.parse(b.createdAt || 0) - Date.parse(a.createdAt || 0));
}
